import React, { Component, createRef } from 'react';
import {
  arrayOf, bool, func, shape, string
} from 'prop-types';
import classNames from 'classnames';
import './media-gallery-overlay.style.scss';
import OverlayHeader from './components/OverlayHeader/OverlayHeader';
import { MainAsset } from './components/MainAsset/MainAsset';
import Thumbnails from '../Thumbnails/Thumbnails';
import Helper from '../../Helper';
import { mediaClick } from '../../../publisher';

const ESCAPE_KEYCODE = 27;

// TODO: Use Overlay component, change to functional, don't require conditional rendering for this to work
class MediaGalleryOverlay extends Component {
  constructor(props) {
    super(props);
    this.overlayRef = createRef();
    const { mainAsset, thumbnails } = props;
    thumbnails.forEach((thumb) => {
      thumb.isSelected = thumb.id === mainAsset.id;
    });
    this.state = {
      isARAssetSelected: !!(mainAsset && Helper.isAR({ media: mainAsset })),
      overlayMainAsset: mainAsset,
      overlayThumbnails: thumbnails,
      isZoomedIn: false
    };
  }

  componentDidMount() {
    const { overlayOpened } = this.props;
    this.disableScroll(overlayOpened);
    document.addEventListener('keydown', this.escFunction, false);
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', this.onPopstate, false);
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.escFunction, false);
    window.removeEventListener('popstate', this.onPopstate, false);
    document.body.style.overflow = 'auto';
  }

  onPopstate = () => {
    const { handleClose, overlayOpened } = this.props;
    if (overlayOpened) {
      handleClose();
    }
  };

  handleThumbnailClick = ({ thumbnailList, thumbnail, mainImages }) => {

    const index = thumbnailList.findIndex((thumb) => thumb.id === thumbnail.id);
    const currentlySelected = thumbnailList.findIndex((thumb) => thumb.isSelected);

    if (index === -1 || currentlySelected === -1) return null;

    // Needed to remove reference from object
    const updatedThumbnails = JSON.parse(JSON.stringify(thumbnailList));

    updatedThumbnails[currentlySelected].isSelected = false;
    updatedThumbnails[index].isSelected = true;

    /* To set analytics position value from thumbnail media gallery */
    const mediaList = thumbnailList.filter((media) => !Helper.isImage({ media }));
    const imageList = thumbnailList.filter((media) => Helper.isImage({ media }));
    const thumbnailMediaList = mediaList.concat(imageList);
    const positionVal = thumbnailMediaList.findIndex((thumb) => thumb.id === thumbnail.id);

    const payload = {
      section: 'expanded-view',
      component: 'thumbnails',
      target: thumbnail.type,
      position: positionVal + 1
    };

    const isAR = thumbnail && Helper.isAR({ media: thumbnail });

    const { isZoomedIn } = this.state;
    if (isZoomedIn) {
      this.setState({
        isZoomedIn: false
      });
    }

    mediaClick(payload);
    this.setState({
      isARAssetSelected: isAR,
      overlayMainAsset: mainImages[index],
      overlayThumbnails: updatedThumbnails
    });

    return null;
  };

  escFunction = (event) => {
    const { overlayOpened } = this.props;

    if (overlayOpened && event && event.keyCode === ESCAPE_KEYCODE) {
      this.handleOverlayClosure();
    }
  };

  handleClickOutside = (event) => {
    const { overlayOpened } = this.props;
    if (!this.overlayRef.current) return;

    if (!this.overlayRef.current.contains(event.target) && event.target.tagName !== 'SPAN') {
      if (overlayOpened) {
        this.handleOverlayClosure();
      }
    }
  };

  handleCloseButtonClick = () => {
    const { overlayOpened } = this.props;
    if (overlayOpened) {
      this.handleOverlayClosure();
    }
  };

  handleOverlayClosure = () => {
    const { handleClose } = this.props;
    // Add a class to the overlay to trigger fadeout animation
    document.querySelector('.overlay--display-block').classList.add('overlay--close-animation');
    // Delay closing the overlay until the fadeout animation has completed
    setTimeout(handleClose, 400);
  };

  handleAssetClick = () => {
    const { overlayMainAsset } = this.state;
    if (Helper.isImage({ media: overlayMainAsset })) {
      this.setState((prev) => {
        return {
          isZoomedIn: !prev.isZoomedIn
        };
      });
    }
  };

  disableScroll = (open) => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
  };

  render() {
    const {
      collection, media, overlayOpened, product, hideProductImagesLabel
    } = this.props;

    const { identifiers } = product;
    const { metadata } = collection || {};
    let brandName = collection ? (metadata || {}).brandName : (identifiers || {}).brandName;
    let productLabel = collection ? (metadata || {}).name : (identifiers || {}).productLabel;
    const { highResolution = [], main, zoom } = media;
    const { overlayMainAsset, overlayThumbnails } = this.state;
    const { id } = overlayMainAsset || {};
    const highResImage = highResolution.find((image) => image.id === id);
    const zoomedAsset = id ? zoom.find((z) => z.id === id) : {};
    const largeImage = highResImage || zoomedAsset || overlayMainAsset;
    const showHideClassName = classNames('overlay', {
      'overlay--display-block': overlayOpened,
      'overlay--hidden': !overlayOpened
    });
    const showPaddingImagesClasses = classNames('overlay__side-content__product-images', {
      'overlay__side-content__product-images--nopadding': hideProductImagesLabel,
      'overlay__side-content__product-images': !hideProductImagesLabel
    });

    const altMedia = [];
    const images = [];
    overlayThumbnails.forEach((thumbnail) => {
      if (Helper.isVideo({ media: thumbnail }) || Helper.isImage360({ media: thumbnail }) || Helper.isAR({ media: thumbnail })) {
        altMedia.push(thumbnail);
      } else {
        images.push(thumbnail);
      }
    });
    const { isARAssetSelected, isZoomedIn } = this.state;
    const hasAltMedia = !!altMedia.length;

    if (overlayOpened) {
      document.addEventListener('click', this.handleClickOutside);
    } else {
      document.removeEventListener('click', this.handleClickOutside);
    }

    return (
      <div className={showHideClassName}>
        <section className="overlay__wrapper" ref={this.overlayRef}>
          <OverlayHeader onClick={this.handleCloseButtonClick} />
          <div className="overlay__content">
            <div className="overlay__main-asset-container">
              <MainAsset
                mainAsset={overlayMainAsset}
                zoomedAsset={largeImage}
                onAssetClick={this.handleAssetClick}
                isZoomedIn={isZoomedIn}
                fwdOverlayRef={this.overlayRef.current}
                collectionLink={overlayMainAsset.collection}
              />
            </div>
            <div className="overlay__side-content-container">
              <div className="overlay__side-content">
                { !hideProductImagesLabel && (
                  <div className="overlay__side-content__label">
                    {productLabel}
                  </div>
                )}
                <div className="overlay__side-content__brand">
                  {brandName
                  && <>by <span className="overlay__side-content__brand--bold">{brandName}</span></>}
                </div>
                {hasAltMedia
                && (
                  <div className="overlay__side-content__alt-media">
                    Related Videos & 360° View
                    <div className="overlay__thumbnails">
                      <Thumbnails
                        thumbnails={altMedia}
                        onThumbnailClick={(thumbnail) => this.handleThumbnailClick({
                          thumbnailList: overlayThumbnails,
                          thumbnail,
                          mainImages: main
                        })}
                      />
                    </div>
                    {isARAssetSelected
                    && (
                      <div className="ar-cta">
                        See it in Your Space (Augmented Reality) is currently
                        <span className="ar-cta__bold-text"> only available using an AR compatible Apple device (iOS 12 or above).</span>
                      </div>
                    )}
                  </div>
                )}
                <div className={showPaddingImagesClasses}>
                  {!hideProductImagesLabel && 'Product Images' }
                  <div className="overlay__thumbnails">
                    <Thumbnails
                      thumbnails={images}
                      onThumbnailClick={(thumbnail) => this.handleThumbnailClick({ thumbnailList: overlayThumbnails, thumbnail, mainImages: main })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }
}

MediaGalleryOverlay.propTypes = {
  collection: shape({}),
  handleClose: func.isRequired,
  mainAsset: shape({
    id: string,
    type: string,
    url: string,
    videoStill: string
  }),
  media: shape({
    main: arrayOf(shape({
      id: string,
      type: string,
      url: string,
      videoStill: string
    })),
    thumbnails: arrayOf(shape({
      id: string,
      type: string,
      url: string,
      videoStill: string
    })),
    zoom: arrayOf(shape({
      id: string,
      type: string,
      url: string,
      videoStill: string
    }))
  }),
  overlayOpened: bool,
  product: shape({}),
  thumbnails: arrayOf(shape({})),
  hideProductImagesLabel: bool
};

MediaGalleryOverlay.defaultProps = {
  collection: null,
  mainAsset: {
    id: '',
    location: '',
    type: ''
  },
  media: {
    main: [{
      id: '',
      location: '',
      type: ''
    }],
    thumbnails: [{
      id: '',
      location: '',
      type: ''
    }],
    zoom: [{
      id: '',
      location: '',
      type: ''
    }]
  },
  overlayOpened: false,
  product: {},
  thumbnails: [],
  hideProductImagesLabel: false
};

export default MediaGalleryOverlay;
